import React, { forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { useLocalizedUrls } from '@/hooks/useUrlTranslations';
import { useLocalizedProductUrls } from '@/hooks/useProductSlugTranslations';

interface LocalizedLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
  children: React.ReactNode;
}

// Map common paths to page keys for URL translation lookup
const PATH_TO_PAGE_KEY: Record<string, string> = {
  '/': 'home',
  '/shop': 'shop',
  '/reviews': 'reviews',
  '/about': 'about',
  '/contact': 'contact',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/refund': 'refund',
  '/withdrawal': 'withdrawal',
  '/cookies': 'cookies',
  '/blog': 'blog',
  '/how-to': 'how-to',
};

/**
 * A Link component that automatically adds the current language prefix 
 * and uses localized slugs from the url_translations table
 */
export const LocalizedLink = forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  ({ to, children, ...props }, ref) => {
    const { getUrl, translations } = useLocalizedUrls();
    const { getProductUrl } = useLocalizedProductUrls();
    
    // Check if this is a product URL
    const productMatch = to.match(/^\/shop\/id-card-generator\/(.+)$/);
    
    if (productMatch) {
      const slug = productMatch[1];
      const localizedTo = getProductUrl(slug);
      return (
        <Link ref={ref} to={localizedTo} {...props}>
          {children}
        </Link>
      );
    }
    
    // Don't localize external links, hash links, or admin routes
    const shouldLocalize = 
      to.startsWith('/') && 
      !to.startsWith('/admin') && 
      !to.startsWith('http') &&
      !to.startsWith('#') &&
      !to.startsWith('/checkout') &&
      !to.startsWith('/auth') &&
      !to.startsWith('/account') &&
      !to.startsWith('/forgot-password') &&
      !to.startsWith('/reset-password');
    
    let localizedTo = to;
    
    if (shouldLocalize && translations.length > 0) {
      const pageKey = PATH_TO_PAGE_KEY[to];
      
      if (pageKey) {
        localizedTo = getUrl(pageKey);
      }
    }
    
    return (
      <Link ref={ref} to={localizedTo} {...props}>
        {children}
      </Link>
    );
  }
);

LocalizedLink.displayName = 'LocalizedLink';
